import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { useState, type ReactNode } from 'react';
import type { Question } from '@/lib/api/types';
import { t } from '@/lib/strings';

interface QuestionCardProps {
  question: Question;
  sectionTitle: string;
  children: ReactNode;
  footer?: ReactNode;
}

/**
 * One question per screen, set large (specification 8.4). The questionnaire is a conversation
 * prop, not a form, so there is nothing else competing for attention and nothing to scroll past
 * during the interview itself.
 */
export function QuestionCard({ question, sectionTitle, children, footer }: QuestionCardProps) {
  const [showFormal, setShowFormal] = useState(false);

  return (
    <Paper variant="outlined" sx={{ p: { xs: 2.5, md: 4 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <Chip label={sectionTitle} size="small" variant="outlined" />
        {question.number && (
          <Typography variant="body2" color="text.secondary">
            {question.number}
          </Typography>
        )}
      </Box>

      <Typography
        component="h2"
        sx={{ fontSize: { xs: '1.5rem', md: '1.85rem' }, fontWeight: 620, lineHeight: 1.3, mb: 2 }}
      >
        {question.text}
      </Typography>

      {/* The everyday illustration sits with the question, because it is part of understanding
          it — not extra reading to be opened if you happen to be unsure. */}
      {question.example && (
        <Typography sx={{ mb: 2, maxWidth: '62ch', fontSize: '1.05rem', lineHeight: 1.55 }}>
          {question.example}
        </Typography>
      )}

      {question.help && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: '62ch' }}>
          {question.help}
        </Typography>
      )}

      {/* The framework's own wording is kept one click away rather than on the page: it is what
          makes the assessment checkable, but it is also what makes it unreadable. */}
      {question.formalText && (
        <Box sx={{ mb: 3 }}>
          <Button
            size="small"
            variant="text"
            onClick={() => setShowFormal((open) => !open)}
            aria-expanded={showFormal}
            sx={{ px: 0 }}
          >
            {showFormal ? t('skema.skjulFagligFormulering') : t('skema.visFagligFormulering')}
          </Button>
          <Collapse in={showFormal}>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, maxWidth: '62ch' }}>
              {question.formalText}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              {t('skema.fagligFormuleringHjaelp')}
            </Typography>
          </Collapse>
        </Box>
      )}

      {children}

      {footer}
    </Paper>
  );
}
